import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

/* =========================================================
   8 INSIGHTS — SEO / AIO META COMPONENT
   Traditional SEO + modern SEO + AI-readability (structured data).
   International standards: Open Graph, Twitter Cards, schema.org JSON-LD.
   Supports full @graph JSON-LD for rich structured data per page.
========================================================= */

const SITE = {
  name: "8 Insights",
  url: "https://8-insights.com",
  logo: "https://8-insights.com/logo512.png",
  defaultImage: "https://8-insights.com/home.jpeg",
  locale: "en_HK",
  email: "info@8-insights.com",
  sameAs: ["https://www.linkedin.com/company/8-insights/"],
};

const DEFAULT_KEYWORDS = [
  "PR agency Hong Kong",
  "global PR agency",
  "marketing communications agency",
  "digital PR services",
  "brand communications",
  "AI technology solutions",
  "marketing consultancy",
  "corporate communications",
  "strategic communications",
  "reputation management",
  "integrated marketing agency",
  "Asia Pacific PR agency",
  "AI-powered marketing solutions",
  "technology consulting",
  "global brand strategy",
];

const SEO = ({
  title = "Global PR, Marketing & AI Technology Solutions",
  description = "8 Insights delivers PR, marketing, communications, and AI-driven technology solutions that help brands grow across Asia Pacific and global markets.",
  url,
  image,
  imageAlt = "8 Insights — Global PR, Marketing and AI Technology Solutions",
  keywords = DEFAULT_KEYWORDS,
  language = "en",
  locale = "en_HK",
  robots = "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
  type = "website",         // website | article
  breadcrumbs,              // [{ name, url }]
  service,                  // { name, description, serviceType } → Service schema
  publishedTime,
  modifiedTime,
  author = "8 Insights",
  publisher = "8 Insights",
  creator = "8 Insights",
  applicationName = "8 Insights",
  themeColor = "#091362",
  referrer = "strict-origin-when-cross-origin",
  formatDetection = "telephone=no",
  bingbot = "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
  googlebotNews = "index,follow",
  googleExtended = "index",
  geoRegion,                // geo.region override e.g. "SG"
  geoPlacename,             // geo.placename override e.g. "Singapore"
  geoPosition,              // geo.position override e.g. "1.3521;103.8198"
  icbm,                     // ICBM override (same coords, comma-separated)
  jobData,
  faqData,
  jsonLd,                   // raw JSON-LD object with @context + @graph (full control)
}) => {
  const location = useLocation();

  // Always append brand — avoids duplication if caller accidentally includes it
  const fullTitle = title
    ? title.includes(SITE.name) ? title : `${title} | ${SITE.name}`
    : SITE.name;
  const finalUrl =
    url || `${SITE.url}${location.pathname === "/" ? "/" : location.pathname}`;
  const finalImage = image || SITE.defaultImage;
  const finalLocale = locale || SITE.locale;
  const kw = Array.isArray(keywords) ? keywords.join(", ") : keywords;

  useEffect(() => {
    document.title = fullTitle;
  }, [fullTitle, location.pathname]);

  // Legacy breadcrumb schema (used by pages that pass breadcrumbs prop without jsonLd)
  const breadcrumbSchema =
    !jsonLd && breadcrumbs && breadcrumbs.length
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: breadcrumbs.map((b, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: b.name,
            ...(b.url ? { item: b.url } : {}),
          })),
        }
      : null;

  return (
    <Helmet prioritizeSeoTags>
      <html lang={language} />

      {/* ── Primary ── */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={kw} />
      <link rel="canonical" href={finalUrl} />

      {/* ── Crawl directives ── */}
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <meta name="bingbot" content={bingbot} />
      <meta name="googlebot-news" content={googlebotNews} />
      <meta name="google-extended" content={googleExtended} />

      {/* ── Authorship / app identity ── */}
      <meta name="author" content={author} />
      <meta name="publisher" content={publisher} />
      <meta name="creator" content={creator} />
      <meta name="application-name" content={applicationName} />

      {/* ── Browser / rendering hints ── */}
      <meta name="theme-color" content={themeColor} />
      <meta name="referrer" content={referrer} />
      <meta name="format-detection" content={formatDetection} />

      {/* ── Geo — defaults to HQ Hong Kong; override per page if needed ── */}
      <meta name="geo.region"    content={geoRegion    || "HK"} />
      <meta name="geo.placename" content={geoPlacename || "Hong Kong"} />
      <meta name="geo.position"  content={geoPosition  || "22.2855;114.1577"} />
      <meta name="ICBM"          content={icbm         || "22.2855, 114.1577"} />

      {/* ── Open Graph ── */}
      <meta property="og:type"          content={publishedTime ? "article" : type} />
      <meta property="og:site_name"     content={SITE.name} />
      <meta property="og:locale"        content={finalLocale} />
      <meta property="og:title"         content={fullTitle} />
      <meta property="og:description"   content={description} />
      <meta property="og:url"           content={finalUrl} />
      <meta property="og:image"         content={finalImage} />
      <meta property="og:image:width"   content="1200" />
      <meta property="og:image:height"  content="630" />
      <meta property="og:image:alt"     content={imageAlt || fullTitle} />

      {/* ── Twitter / X Cards ── */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={finalImage} />

      {/* ── Full @graph JSON-LD (preferred — covers Organization, WebSite, WebPage, FAQ, etc.) ── */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}

      {/* ── Legacy per-schema props (used by other pages that don't pass jsonLd yet) ── */}
      {!jsonLd && breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}

      {!jsonLd && service && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: service.name,
            description: service.description || description,
            serviceType: service.serviceType || service.name,
            provider: { "@id": `${SITE.url}/#organization` },
            areaServed: "Worldwide",
            url: finalUrl,
          })}
        </script>
      )}

      {!jsonLd && publishedTime && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: title,
            description,
            image: [finalImage],
            author: author ? { "@type": "Person", name: author } : undefined,
            publisher: { "@id": `${SITE.url}/#organization` },
            datePublished: publishedTime,
            dateModified: modifiedTime || publishedTime,
            mainEntityOfPage: finalUrl,
          })}
        </script>
      )}

      {!jsonLd && jobData && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "JobPosting",
            ...jobData,
          })}
        </script>
      )}

      {!jsonLd && faqData && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqData,
          })}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
